import { DropdownProps } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ErrorMessage from '../../../../../../../../../../../../components/error-message';
import {
  IEnergyEmission,
  useListEnergyEmissions,
} from '../../../../../../../../../../../../hooks/api/energy-emissions';
import { useFootprintAndProduct } from '../../../../../../../../../../../../hooks/useFootprintAndProduct';
import { CountryCodeToLabelMap } from '../../../../../../../../../../../../shared/helpers/country-region-helpers';
import { filterEnergyEmissions } from '../../helpers';
import Menu from './Menu';
import styles from './UseOfEnergyDropdown.module.css';

interface UseOfEnergyDropdownProps extends DropdownProps {
  name: string;
  productCountryCode?: string;
}

const UseOfEnergyDropdown = (props: UseOfEnergyDropdownProps) => {
  const { name, productCountryCode } = props;
  const { t } = useTranslation();

  const { control, watch } = useFormContext();

  // Listen for reference period changes
  const selectedReferencePeriodYear = watch('reference_period')?.getFullYear();

  // Boolean indicating if the dropdown is disabled based on the reference period
  // The dropdown is disabled if no reference period year is selected
  const isDisabled = !selectedReferencePeriodYear;

  const {
    field: { value: inputValue, ref, onChange },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
  });

  const { data: energyEmissions, isLoading } = useListEnergyEmissions();
  const { footprintDetails, calculationDetails } = useFootprintAndProduct();

  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const [filteredEnergyEmissions, setFilteredEnergyEmissions] = useState<IEnergyEmission[]>(
    energyEmissions || [],
  );
  const [searchString, setSearchString] = useState<string>('');

  // Once energy emissions are loaded, filter them by year and country code
  useEffect(() => {
    const _filteredEnergyEmissions = filterEnergyEmissions(
      energyEmissions,
      selectedReferencePeriodYear,
      productCountryCode,
    );

    setFilteredEnergyEmissions(_filteredEnergyEmissions);

    // Get the id of the last created custom energy emission from session storage to auto-select it
    // Then remove it from the session storage
    const lastCreatedEnergyEmissionId = sessionStorage.getItem('lastCreatedEnergyEmissionId');

    if (lastCreatedEnergyEmissionId) {
      // Find the corresponding object from fetched list
      const latestCreatedEnergyEmission = energyEmissions?.find((emission) => {
        return emission.id === Number(lastCreatedEnergyEmissionId);
      });

      if (latestCreatedEnergyEmission) {
        // If found, auto-select it
        onChange(latestCreatedEnergyEmission);
        // Clean the session storage
        sessionStorage.removeItem('lastCreatedEnergyEmissionId');

        return;
      }
    }
  }, [energyEmissions, selectedReferencePeriodYear, productCountryCode]);

  // Listen for reference period changes
  useEffect(() => {
    // Check if the previously saved reference period year is different from the current selected one
    const previouslySavedYear = new Date(
      calculationDetails?.data?.reference_period_start || 0,
    ).getFullYear();

    const isChanged = previouslySavedYear !== selectedReferencePeriodYear;
    const isSecondaryBasedEnergySelected = !!inputValue?.countryCode;

    // If year is changed and current selected energy is a secondary-based energy, reset the energy dropdown selection
    if (isChanged && isSecondaryBasedEnergySelected) {
      onChange(null);
    }
  }, [selectedReferencePeriodYear, footprintDetails]);

  const onInputMouseDown = (): void => {
    if (isDisabled) return;

    setIsMenuVisible(true);
  };

  const onInputBlur = (): void => {
    setIsMenuVisible(false);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    if (!isMenuVisible) setIsMenuVisible(true);

    setSearchString(value);

    if (value !== inputValue?.name) {
      // Unselect the value if user types something in the input right after selecting an option
      onChange(null);
    }

    // Filter emissions based on search string
    setFilteredEnergyEmissions(() => {
      // First filter by year and country code
      let _filteredEnergyEmissions = filterEnergyEmissions(
        energyEmissions,
        selectedReferencePeriodYear,
        productCountryCode,
      );

      // Then filter by search string
      _filteredEnergyEmissions = _filteredEnergyEmissions?.filter((emission) => {
        const name = emission.name;
        const country = CountryCodeToLabelMap[emission.countryCode as string] || '';
        const year = emission.year;

        // Combine emission name, country, year for search
        const searchSource = `${name} ${country} | ${year?.toString()}`.toLowerCase();

        return searchSource.includes(value.trim().toLowerCase());
      });

      return _filteredEnergyEmissions || [];
    });
  };

  const onTriggerMouseDown = (event: React.MouseEvent<HTMLElement>): void => {
    if (isDisabled) return;

    event.stopPropagation();
    setIsMenuVisible(!isMenuVisible);
  };

  const onItemMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
  };

  const onSearchMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    // Close the menu if "Esc" is pressed
    if (e.key === 'Escape') {
      setIsMenuVisible(false);
    }
  };

  const onItemSelect = (item: Partial<IEnergyEmission>): void => {
    onChange(item);
    setSearchString(item?.name || '');
    setIsMenuVisible(false);
  };

  // Function to what to show on the dropdown. Considers the selected item and/or search string
  const determineDropdownValue = (): string | undefined => {
    if (inputValue?.name) {
      return inputValue.name;
    }

    if (inputValue?.countryCode) {
      return `${CountryCodeToLabelMap[inputValue.countryCode]} | ${inputValue?.year}` || '';
    }

    if (searchString) {
      return searchString;
    }

    return '';
  };

  if (isLoading) {
    return <Skeleton className='w-full' height='45px' />;
  }

  return (
    <div>
      <div className='p-fluid mb-1' onBlur={onInputBlur}>
        <div
          className={classNames(
            'flex flex-row align-items-center p-float-label',
            styles['input-wrapper'],
          )}
          onMouseDown={onInputMouseDown}
          onKeyDown={onKeyDown}
          ref={ref}
          aria-disabled={isDisabled}
        >
          <InputText
            ref={ref}
            name={name}
            type='text'
            onChange={onInputChange}
            value={determineDropdownValue()}
            style={{ textIndent: '2rem' }}
            className={classNames('line-height-1 text-overflow-ellipsis w-full', {
              'p-invalid': error,
            })}
            disabled={isDisabled}
          />
          <label htmlFor={name}>
            {t('calculationWizard.sections.generalInformationSection.electricity')}
          </label>

          {/* Search Icon */}
          <div
            className='flex align-items-center px-2 absolute left-0 mx-3 text-gray-400 z-1'
            onMouseDown={onSearchMouseDown}
          >
            <i className='pi pi-search' />
          </div>

          {/* Trigger Icon */}
          <div
            className='flex align-items-center  absolute right-0 px-2 py-2 mr-3 text-gray-400 z-1'
            onMouseDown={onTriggerMouseDown}
          >
            <i className={classNames('pi', isMenuVisible ? 'pi-angle-up' : 'pi-angle-down')} />
          </div>
        </div>
        {/* Dropdown Menu */}
        {isMenuVisible && (
          <Menu
            filteredEnergyEmissions={filteredEnergyEmissions}
            onItemMouseDown={onItemMouseDown}
            onItemSelect={onItemSelect}
          />
        )}
      </div>
      {invalid && (
        <ErrorMessage
          message={t(
            'calculationWizard.sections.generalInformationSection.inputs.errorMessages.useOfEnergyRequired',
          )}
        />
      )}
    </div>
  );
};

export default UseOfEnergyDropdown;
